import { Box, Typography } from '@material-ui/core';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import { FunctionComponent, useEffect, useState } from 'react';
import VaccineDetailTable from './VaccineDetailTable';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'store';
import { getVaccineReport } from 'actions/insights/clientsAndPets/vaccineStatus/VaccineReportActions';
import { useApiClientWrapper, useMarketplace } from 'hooks';
import { VaccineReport, VaccinesReport } from 'model/ClientAndPetsReport';
import { Pagination, Period } from '@spike/model';
import moment from 'moment-timezone';
import { useTimeZone } from 'hooks';
import { VaccineReportStatus, VaccineReportState } from 'reducers/vaccineReport/VaccineReportState';
import Header from '../../components/Header';

interface VaccineStatusProps {
    description?: string;
}

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        container: {
            display: 'flex',
            flexDirection: 'column',
            width: '100%',
            backgroundColor: 'white',
            height: 'fit-content',

            [theme.breakpoints.up('md')]: {
                //padding: '50px 24px'
            }
        },
        mobileWrapper: {
            [theme.breakpoints.down('sm')]: {
                marginTop: 24
            }
        }
    })
);

export const VaccineStatus: FunctionComponent<VaccineStatusProps> = props => {
    const classes = useStyles();

    const dispatch = useDispatch();

    const timeZone = useTimeZone();

    const vaccineLoading = useSelector<RootState, boolean>(state => state.vaccineReport.loading);

    const vaccineStatus = useSelector<RootState, VaccineReportStatus>(state => state.vaccineReport.status);

    const vaccineReport = useSelector<RootState, VaccineReportState>(state => state.vaccineReport);

    const [period, setPeriod] = useState<Period | null>({
        from: moment().tz(timeZone).startOf('month'),
        to: moment().tz(timeZone).endOf('month')
    });

    const [veccinesReportConverted, setVaccinesReportConverted] = useState<Array<VaccineReport> | null>(null);

    const [page, setPage] = useState(1);

    const [pageSize, setPageSize] = useState(10);

    const [pagination, setPagination] = useState<Pagination>({
        page: Number(vaccineReport.page),
        pageSize: Number(vaccineReport.size),
        totalPages: Number(vaccineReport.totalPages),
        totalRecords: Number(vaccineReport.totalRecords)
    });

    const apiClientWrapper = useApiClientWrapper();
    const marketplace = useMarketplace();

    let url = `/pet_vaccines_export_report?marketplace_id=${apiClientWrapper.marketplaceId}`;
    if (period?.from) url += `&start_date=${period?.from.format('YYYY/MM/DD')}`;
    if (period?.to) url += `&end_date=${period?.to.format('YYYY/MM/DD')}`;

    const exportFile = {
        name: `Vaccine Status Information ${marketplace.basics.businessName}.csv`,
        url: url,
        body: {
            conditions: {
                deceased: false,
                deleted: false,
                active: true
            }
        },
        apiClientWrapper: apiClientWrapper
    };

    const calendar = {
        optionSelected: 'This month'
    };

    useEffect(() => {
        dispatch(getVaccineReport(page, pageSize, period?.from.format('YYYY/MM/DD'), period?.to.format('YYYY/MM/DD')));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [period, page, pageSize]);

    useEffect(() => {
        if (vaccineStatus === VaccineReportStatus.GettingSuccess) {
            setVaccinesReportConverted(vaccineReport.pets);
            setPagination({
                page: Number(vaccineReport.page),
                pageSize: Number(vaccineReport.size),
                totalPages: Number(vaccineReport.totalPages),
                totalRecords: Number(vaccineReport.totalRecords)
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [vaccineStatus]);

    const handleChangePeriod = (dateFilter: Period | null, allTime: boolean) => {
        if (allTime) {
            setPeriod(null);
        } else {
            setPeriod({
                from: dateFilter?.from || moment(),
                to: dateFilter?.to || moment()
            });
        }
    };

    const changePageHandler = (page: number) => {
        setPage(page);
    };

    const changePageSizeHandler = (pageSize: number) => {
        setPageSize(pageSize);
        setPage(1);
    };

    return (
        <Box className={classes.container}>
            <Box>
                <Typography color="textSecondary"> {props.description} </Typography>
            </Box>
            <Box className={classes.mobileWrapper}>
                <Header
                    period={period}
                    onChangePeriod={handleChangePeriod}
                    exportFile={exportFile}
                    calendar={calendar}
                    hideAllTime={true}
                />
            </Box>
            <VaccineDetailTable
                loading={vaccineLoading}
                clients={veccinesReportConverted}
                pagination={pagination}
                changePageHandler={changePageHandler}
                changePageSizeHandler={changePageSizeHandler}
            />
        </Box>
    );
};

export default VaccineStatus;
